"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./Tooltip.module.css";

export default function Tooltip({
  children,
  label,
  placement = "below",
  fullWidth = false,
  as: Component = "span",
}) {
  const tooltipRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [tooltipSize, setTooltipSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!isHovered) return undefined;
    const measure = () => {
      const rect = tooltipRef.current?.getBoundingClientRect();
      if (rect) setTooltipSize({ width: rect.width, height: rect.height });
    };
    const rafId = requestAnimationFrame(measure);
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", measure);
    };
  }, [isHovered, label]);

  useEffect(() => {
    if (!isHovered) {
      setIsActive(false);
      return undefined;
    }
    const frameId = requestAnimationFrame(() => setIsActive(true));
    return () => cancelAnimationFrame(frameId);
  }, [isHovered]);

  useEffect(() => {
    if (!isHovered) return undefined;
    const onMove = (event) => setMousePosition({ x: event.clientX, y: event.clientY });
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [isHovered]);

  const computeStyle = () => {
    const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 0;
    const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 0;
    const margin = 8;
    const gap = 12;

    let left = mousePosition.x;
    let top = mousePosition.y;
    let transform = "";

    if (placement === "right") {
      left = mousePosition.x + gap;
      top = mousePosition.y;
      if (tooltipSize.width > 0 && viewportWidth > 0) {
        const maxLeft = viewportWidth - margin - tooltipSize.width;
        left = Math.min(Math.max(left, margin), maxLeft);
      }
      if (tooltipSize.height > 0 && viewportHeight > 0) {
        const minTop = margin + tooltipSize.height / 2;
        const maxTop = viewportHeight - margin - tooltipSize.height / 2;
        top = Math.min(Math.max(top, minTop), maxTop);
      }
      transform = `translate(${isActive ? "0px" : "6px"}, -50%)`;
    } else if (placement === "left") {
      left = mousePosition.x - gap;
      top = mousePosition.y;
      if (tooltipSize.width > 0 && viewportWidth > 0) {
        const minLeft = margin + tooltipSize.width;
        left = Math.max(left, minLeft);
        const maxLeft = viewportWidth - margin;
        left = Math.min(left, maxLeft);
      }
      if (tooltipSize.height > 0 && viewportHeight > 0) {
        const minTop = margin + tooltipSize.height / 2;
        const maxTop = viewportHeight - margin - tooltipSize.height / 2;
        top = Math.min(Math.max(top, minTop), maxTop);
      }
      transform = `translate(${isActive ? "-100%" : "calc(-100% + 6px)"}, -50%)`;
    } else if (placement === "above") {
      const gapAbove = 10;
      left = mousePosition.x;
      top = mousePosition.y - gapAbove;
      if (tooltipSize.width > 0 && viewportWidth > 0) {
        const halfW = tooltipSize.width / 2;
        const minLeft = margin + halfW;
        const maxLeft = viewportWidth - margin - halfW;
        left = Math.min(Math.max(left, minLeft), maxLeft);
      }
      if (tooltipSize.height > 0 && viewportHeight > 0) {
        const minTop = margin + tooltipSize.height;
        top = Math.max(top, minTop);
      }
      transform = `translate(-50%, ${isActive ? "-100%" : "calc(-100% + 6px)"})`;
    } else {
      const gapBelow = 10;
      left = mousePosition.x;
      top = mousePosition.y + gapBelow;
      if (tooltipSize.width > 0 && viewportWidth > 0) {
        const halfW = tooltipSize.width / 2;
        const minLeft = margin + halfW;
        const maxLeft = viewportWidth - margin - halfW;
        left = Math.min(Math.max(left, minLeft), maxLeft);
      }
      if (tooltipSize.height > 0 && viewportHeight > 0) {
        const maxTop = viewportHeight - margin - tooltipSize.height;
        top = Math.min(top, maxTop);
      }
      transform = `translate(-50%, ${isActive ? "0px" : "6px"})`;
    }

    return {
      top: `${top}px`,
      left: `${left}px`,
      transform,
      opacity: isActive ? 1 : 0,
    };
  };

  return (
    <Component
      className={styles.anchor}
      style={fullWidth ? { display: "block", width: "100%" } : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
      {label && isHovered && typeof document !== "undefined"
        ? createPortal(
            <div ref={tooltipRef} className={styles.tooltip} style={computeStyle()}>
              {label}
            </div>,
            document.body
          )
        : null}
    </Component>
  );
}
